import { Skeleton } from "@/components/ui/skeleton";
import { useCustomerHours } from "@/hooks/use-customer-hours";
import { useCustomerOfferGeek } from "@/hooks/use-customer-offer-geek";
import { customerGeek } from "@/lib/customer-kpi-calc";
import { formatDateShort, formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { DBProject } from "@/lib/db-types";

const DASH = "–";

interface CustomerGeekStripProps {
  customerId: string;
  projects: DBProject[];
}

function Tile({ label, value, sub, accent = false }: { label: string; value: string; sub: string; accent?: boolean }) {
  return (
    <div className={cn("kpi-card flex min-w-0 flex-col gap-2 !p-4", accent && "accent")}>
      <div className="label">{label}</div>
      <div className="font-display text-[28px] font-semibold leading-none tracking-tight tabular-nums whitespace-nowrap">{value}</div>
      <div className={cn("text-xs", accent ? "opacity-85" : "text-muted-foreground")}>{sub}</div>
    </div>
  );
}

/**
 * GEEK-stripen i heroen (kundekort v3): fire like fliser, alle GEEK-baserte.
 * Pipe = vektet GEEK estimert på sendte tilbud, levert = bf_actual på
 * avsluttede, i arbeid = bf_estimated på pågående, timepris = GEEK per logget
 * time. Formlene bor i customer-kpi-calc.ts og use-customer-offer-geek.ts.
 */
export default function CustomerGeekStrip({ customerId, projects }: CustomerGeekStripProps) {
  const pipe = useCustomerOfferGeek(customerId);
  const { data: hours, isLoading: hoursLoading } = useCustomerHours(customerId, projects.map((p) => p.id));

  if (hoursLoading || pipe.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3" aria-label="Laster nøkkeltall">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }

  const geek = customerGeek(
    projects.map((p) => ({ ...p, is_internal: p.is_internal, project_status: p.project_status ?? "", completed_at: p.completed_at })),
    hours ?? new Map(),
  );
  const { value, offerCount, nextCloseDate } = pipe;
  const pipeSub = offerCount === 0
    ? "Ingen tilbud ute"
    : [`${offerCount} ${offerCount === 1 ? "tilbud" : "tilbud"}`, nextCloseDate ? `neste close ${formatDateShort(nextCloseDate)}` : null, value.uncategorizedCount > 0 ? `${value.uncategorizedCount} uten sannsynlighet` : null]
        .filter(Boolean)
        .join(" · ");

  return (
    <section aria-label="Nøkkeltall" className="grid grid-cols-2 gap-3">
      <Tile label="GEEK i pipe nå" value={formatNOK(value.weightedKr)} sub={pipeSub} accent />
      <Tile
        label="GEEK levert"
        value={geek.deliveredKr != null ? formatNOK(geek.deliveredKr) : DASH}
        sub={geek.deliveredKr != null
          ? `${formatNOK(geek.deliveredContractKr)} i kontrakt · ${geek.deliveredCount} ${geek.deliveredCount === 1 ? "prosjekt" : "prosjekter"}`
          : "Ingen avsluttede prosjekter ennå"}
      />
      <Tile
        label="GEEK i arbeid"
        value={geek.inProgressKr != null ? formatNOK(geek.inProgressKr) : DASH}
        sub={geek.inProgressCount > 0 ? `estimert på ${geek.inProgressCount} ${geek.inProgressCount === 1 ? "pågående" : "pågående"}` : "Ingen pågående prosjekter"}
      />
      <Tile
        label="Timepris reell"
        value={geek.realHourlyRate != null ? formatNOK(Math.round(geek.realHourlyRate)) : DASH}
        sub={geek.realHourlyRate != null ? "GEEK per logget time" : "Ingen timer på avsluttede"}
      />
    </section>
  );
}
